import { create } from 'zustand';
import { api } from '../services/api';
import type { FollowUp } from '../types';

interface FollowUpsState {
  followups: FollowUp[];
  pendingCount: number;
  isLoading: boolean;
  error: string | null;
  fetchFollowUps: (params?: { status?: FollowUp['status'] }) => Promise<void>;
  fetchPendingCount: () => Promise<void>;
  fetchDueToday: () => Promise<FollowUp[]>;
  createFollowUp: (data: Omit<FollowUp, 'id' | 'createdAt' | 'updatedAt' | 'completedAt'>) => Promise<void>;
  updateFollowUp: (id: string, data: Partial<FollowUp>) => Promise<void>;
  deleteFollowUp: (id: string) => Promise<void>;
}

export const useFollowUpsStore = create<FollowUpsState>((set) => ({
  followups: [],
  pendingCount: 0,
  isLoading: false,
  error: null,

  fetchFollowUps: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      const followups = await api.get<FollowUp[]>(`/api/followups?${query}`);
      set({ followups, isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  fetchPendingCount: async () => {
    const { count } = await api.get<{ count: number }>('/api/followups/pending-count');
    set({ pendingCount: count });
  },

  fetchDueToday: async () => {
    return api.get<FollowUp[]>('/api/followups/due-today');
  },

  createFollowUp: async (data) => {
    const followup = await api.post<FollowUp>('/api/followups', data);
    set(state => ({ followups: [followup, ...state.followups] }));
  },

  updateFollowUp: async (id, data) => {
    await api.patch(`/api/followups/${id}`, data);
    set(state => ({ followups: state.followups.map(f => f.id === id ? { ...f, ...data } : f) }));
  },

  deleteFollowUp: async (id) => {
    await api.delete(`/api/followups/${id}`);
    set(state => ({ followups: state.followups.filter(f => f.id !== id) }));
  },
}));